import { useQueryGetAllBots } from "@apis:bot/bot";
import {
  BrandingConfig,
  PageConfig,
  PageKey,
} from "@apis:ui_branding/ui_branding";
import {
  useQueryGetUiBrandingByBot,
  useQueryGetUiBrandingByOrganization,
} from "@apis:ui_branding/ui_branding";
import { useBrowserBranding } from "@hooks:useBrowserBranding";
import { useUser } from "@libs:react-query-auth";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { mergeBrandingConfigs, mergePageConfigs } from "./mergeBranding";

interface BrandingContextValue {
  config: BrandingConfig | null;
  isLoading: boolean;
  hasBranding: boolean;
  getPageConfig: (page: PageKey) => PageConfig | null;
  /** Set the current bot ID for branding (called by BotSelectionPersistent or Analytics) */
  setCurrentBotId: (botId: string | null) => void;
  isAllBotsSameOrg: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  config: null,
  isLoading: true,
  hasBranding: false,
  getPageConfig: () => null,
  setCurrentBotId: () => {},
  isAllBotsSameOrg: false,
});

export const useBranding = () => useContext(BrandingContext);

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider = ({ children }: BrandingProviderProps) => {
  const user = useUser();
  const isLoggedIn = !!user.data;

  // Current bot ID set by components inside the router (BotSelectionPersistent, Analytics)
  const [currentBotId, setCurrentBotIdState] = useState<string | null>(null);

  // Memoize to ensure stable reference for effect dependencies
  const setCurrentBotId = useCallback((botId: string | null) => {
    setCurrentBotIdState(botId);
  }, []);

  // Fetch all bots to find selected bot's organization
  const { data: bots, isFetched: botsFetched } = useQueryGetAllBots(
    true,
    undefined,
    { enabled: isLoggedIn },
  );

  const isAllBotsSameOrg = useMemo(() => {
    if (!bots || bots.length === 0) return false;

    const orgIds = new Set(bots.map((bot) => bot.organization_id));
    return orgIds.size === 1;
  }, [bots]);

  // Find organization ID from selected bot
  const organizationId = useMemo(() => {
    if (!currentBotId || !bots || bots.length === 0) return null;

    const selectedBot = bots.find((bot) => bot.id === currentBotId);
    return selectedBot?.organization_id ?? null;
  }, [currentBotId, bots]);

  // Fetch org-level branding for the organization
  const { data: orgBranding, isFetched: orgBrandingFetched } =
    useQueryGetUiBrandingByOrganization(
      { organizationId: organizationId ?? "" },
      { enabled: !!organizationId },
    );

  // Fetch bot-level branding for the selected bot (takes precedence)
  const { data: botBranding, isFetched: botBrandingFetched } =
    useQueryGetUiBrandingByBot(
      { botId: currentBotId ?? "" },
      { enabled: !!currentBotId },
    );

  // Effective config: field-level merge, bot branding over org branding
  const config = useMemo<BrandingConfig | null>(
    () => mergeBrandingConfigs(botBranding?.config, orgBranding?.config),
    [botBranding?.config, orgBranding?.config],
  );

  const getPageConfig = useCallback(
    (page: PageKey): PageConfig | null => {
      return mergePageConfigs(
        botBranding?.config?.pages?.[page],
        orgBranding?.config?.pages?.[page],
      );
    },
    [botBranding?.config?.pages, orgBranding?.config?.pages],
  );

  const isLoading =
    isLoggedIn &&
    (!botsFetched ||
      (!!organizationId && !orgBrandingFetched) ||
      (!!currentBotId && !botBrandingFetched));

  // Apply browser title and favicon from branding config
  const [browserConfig, setBrowserConfig] = useState<BrandingConfig | null>(
    null,
  );
  useEffect(() => {
    if (!isLoading) setBrowserConfig(config);
  }, [isLoading, config]);
  useBrowserBranding(browserConfig);

  const contextValue = useMemo<BrandingContextValue>(
    () => ({
      config,
      isLoading,
      hasBranding: !!(config?.primaryColor || config?.logoBase64),
      getPageConfig,
      setCurrentBotId,
      isAllBotsSameOrg,
    }),
    [config, isLoading, getPageConfig, setCurrentBotId, isAllBotsSameOrg],
  );

  return (
    <BrandingContext.Provider value={contextValue}>
      {children}
    </BrandingContext.Provider>
  );
};
